import React from 'react';
import { FaDollarSign, FaInfoCircle } from 'react-icons/fa';
import { IncomeData } from '../types';
import { OVERTIME_FACTOR_OPTIONS, VALIDATION_LIMITS, STATE_ADJUSTMENT_FACTORS } from '../utils/constants';
import { calculateTotalGrossIncome, calculateAdjustedMonthlyIncome, formatCurrency } from '../utils/calculations';

interface RealIncomeCalculatorProps {
  income: Partial<IncomeData>;
  onIncomeChange: (income: Partial<IncomeData>) => void;
  selectedState?: string;
}

const RealIncomeCalculator: React.FC<RealIncomeCalculatorProps> = ({
  income,
  onIncomeChange,
  selectedState
}) => {
  // Auto-populate regional adjustment factor based on state
  React.useEffect(() => {
    if (selectedState && STATE_ADJUSTMENT_FACTORS[selectedState]) {
      onIncomeChange({
        ...income,
        regionalCostAdjustmentFactor: STATE_ADJUSTMENT_FACTORS[selectedState]
      });
    }
  }, [selectedState]);

  // Calculate totals for display
  const totalGrossIncome = React.useMemo(() => {
    if (
      income.primaryEarnerBaseSalary &&
      income.overtimeFactor !== undefined &&
      income.bonusIncentiveAnnual !== undefined &&
      income.secondaryEarnerSalary !== undefined &&
      income.sideWorkFreelanceMonthly !== undefined
    ) {
      return calculateTotalGrossIncome(income as IncomeData);
    }
    return 0;
  }, [income]);

  const adjustedMonthlyIncome = React.useMemo(() => {
    if (totalGrossIncome > 0 && income.regionalCostAdjustmentFactor) {
      return calculateAdjustedMonthlyIncome(income as IncomeData);
    }
    return 0;
  }, [totalGrossIncome, income.regionalCostAdjustmentFactor]);

  return (
    <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-100">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-6">
        <FaDollarSign className="text-blue-600 text-xl" style={{ color: '#1e3a8a' }} />
        <h2 className="text-2xl font-semibold text-gray-900">Real Household Income Calculator</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Primary Earner Base Salary */}
        <div>
          <label className="label">
            Primary Earner Base Salary (Annual)
            <FaInfoCircle 
              className="inline ml-1 text-gray-400 text-xs cursor-help" 
              title="Annual gross salary before taxes and deductions"
            />
          </label>
          <input 
            type="number" 
            className="input-field"
            style={{ backgroundColor: '#bfdbfe' }}
            placeholder="$50,000"
            min={VALIDATION_LIMITS.salary.min}
            max={VALIDATION_LIMITS.salary.max}
            value={income.primaryEarnerBaseSalary || ''}
            onChange={(e) => onIncomeChange({ 
              ...income, 
              primaryEarnerBaseSalary: parseFloat(e.target.value) || 0 
            })}
          />
          <small className="text-gray-500 text-xs">
            Range: ${VALIDATION_LIMITS.salary.min.toLocaleString()} - ${VALIDATION_LIMITS.salary.max.toLocaleString()}
          </small>
        </div>

        {/* Overtime Factor */}
        <div>
          <label className="label">
            Overtime Factor (%)
            <FaInfoCircle 
              className="inline ml-1 text-gray-400 text-xs cursor-help" 
              title="Include all overtime, typically 15-25% for trade workers"
            />
          </label>
          <select 
            className="input-field"
            style={{ backgroundColor: '#bfdbfe' }}
            value={income.overtimeFactor || 0}
            onChange={(e) => onIncomeChange({ 
              ...income, 
              overtimeFactor: parseFloat(e.target.value) 
            })}
          >
            {OVERTIME_FACTOR_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <small className="text-gray-500 text-xs">Typical range: 15-25% for trade workers</small>
        </div>

        {/* Bonus/Incentive Annual */}
        <div>
          <label className="label">
            Bonus/Incentive (Annual)
            <FaInfoCircle 
              className="inline ml-1 text-gray-400 text-xs cursor-help" 
              title="Performance bonuses, profit sharing, or incentive pay"
            />
          </label>
          <input 
            type="number" 
            className="input-field"
            style={{ backgroundColor: '#bfdbfe' }}
            placeholder="$3,000"
            min="0"
            value={income.bonusIncentiveAnnual || ''}
            onChange={(e) => onIncomeChange({ 
              ...income, 
              bonusIncentiveAnnual: parseFloat(e.target.value) || 0 
            })}
          />
          <small className="text-gray-500 text-xs">Performance bonuses, profit sharing, etc.</small>
        </div>

        {/* Secondary Earner Salary */}
        <div>
          <label className="label">
            Secondary Earner Salary (Annual)
            <FaInfoCircle 
              className="inline ml-1 text-gray-400 text-xs cursor-help" 
              title="Spouse or partner's annual gross salary"
            />
          </label>
          <input 
            type="number" 
            className="input-field"
            style={{ backgroundColor: '#bfdbfe' }}
            placeholder="$35,000"
            min="0"
            value={income.secondaryEarnerSalary || ''}
            onChange={(e) => onIncomeChange({ 
              ...income, 
              secondaryEarnerSalary: parseFloat(e.target.value) || 0 
            })}
          />
          <small className="text-gray-500 text-xs">Spouse or partner's annual income</small>
        </div>

        {/* Side Work/Freelance Monthly */}
        <div>
          <label className="label">
            Side Work/Freelance (Monthly)
            <FaInfoCircle 
              className="inline ml-1 text-gray-400 text-xs cursor-help" 
              title="Use reliable monthly income only - cash work, side jobs"
            />
          </label>
          <input 
            type="number" 
            className="input-field"
            style={{ backgroundColor: '#bfdbfe' }}
            placeholder="$500"
            min="0"
            value={income.sideWorkFreelanceMonthly || ''}
            onChange={(e) => onIncomeChange({ 
              ...income, 
              sideWorkFreelanceMonthly: parseFloat(e.target.value) || 0 
            })}
          />
          <small className="text-gray-500 text-xs">Cash work, side jobs - reliable income only</small>
        </div>

        {/* Regional Cost Adjustment Factor */}
        <div>
          <label className="label">
            Regional Cost Adjustment Factor
            <FaInfoCircle 
              className="inline ml-1 text-gray-400 text-xs cursor-help" 
              title="Auto-populated by zip/city - adjusts for local cost of living"
            />
          </label>
          <input 
            type="number" 
            className="input-field"
            style={{ backgroundColor: '#f3f4f6' }}
            placeholder="1.00"
            step="0.01"
            min={VALIDATION_LIMITS.regionalAdjustment.min}
            max={VALIDATION_LIMITS.regionalAdjustment.max}
            value={income.regionalCostAdjustmentFactor || ''}
            onChange={(e) => onIncomeChange({ 
              ...income, 
              regionalCostAdjustmentFactor: parseFloat(e.target.value) || 1.0 
            })}
            readOnly={selectedState ? !!STATE_ADJUSTMENT_FACTORS[selectedState] : false}
          />
          <small className="text-gray-500 text-xs">
            {selectedState ? `Auto-set for ${selectedState}` : 'Range: 0.75 - 1.35'}
          </small>
        </div>
      </div>

      {/* Calculations Display */}
      <div className="mt-8 pt-6 border-t border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Total Gross Household Income */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">TOTAL GROSS HOUSEHOLD INCOME</h3>
            <div 
              className="text-2xl font-bold"
              style={{ color: totalGrossIncome > 0 ? '#16a34a' : '#6b7280' }}
            >
              {totalGrossIncome > 0 ? formatCurrency(totalGrossIncome) : '$0'}
            </div>
            <small className="text-gray-500">Annual gross income</small>
          </div>

          {/* Adjusted Monthly Income */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 mb-2">ADJUSTED MONTHLY INCOME</h3>
            <div 
              className="text-2xl font-bold"
              style={{ color: adjustedMonthlyIncome > 0 ? '#16a34a' : '#6b7280' }}
            >
              {adjustedMonthlyIncome > 0 ? formatCurrency(adjustedMonthlyIncome) : '$0'}
            </div>
            <small className="text-gray-500">After cost of living adjustment</small>
          </div>
        </div>

        {/* Formula Display */}
        {totalGrossIncome > 0 && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Calculation Formula:</h4>
            <div className="text-xs text-blue-800 font-mono">
              Total Gross = Primary×(1+Overtime%) + Bonus + Secondary + (SideWork×12)<br/>
              = ${income.primaryEarnerBaseSalary?.toLocaleString() || 0}×(1+{((income.overtimeFactor || 0) * 100).toFixed(0)}%) + 
              ${income.bonusIncentiveAnnual?.toLocaleString() || 0} + 
              ${income.secondaryEarnerSalary?.toLocaleString() || 0} + 
              (${income.sideWorkFreelanceMonthly?.toLocaleString() || 0}×12)
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RealIncomeCalculator;